import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl } from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader, EmptyState, SectionHeading } from "../components/Loader";
import { StatusPill } from "../components/StatusPill";
import { ProgressRing } from "../components/Progress";
import { Timeline } from "../components/Timeline";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { fmtDate } from "../lib/format";
import { ImageIcon, PlayCircle, Hammer } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";

function PhaseRow({ p }) {
  return (
    <div className="flex items-center gap-4 py-4 border-b border-border last:border-0" data-testid={`phase-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-arq-ink">{p.name}</p>
          <StatusPill status={p.status} />
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {fmtDate(p.start_date)} → {fmtDate(p.actual_completion || p.estimated_completion)}
          {p.actual_completion ? " (completed)" : " (est.)"}
        </p>
      </div>
      <div className="w-28 shrink-0">
        <div className="flex items-center justify-between text-xs mb-1"><span className="text-muted-foreground">{p.completion_percentage}%</span></div>
        <div className="h-1.5 bg-arq-surface rounded-full overflow-hidden">
          <div className="h-full bg-arq-green rounded-full" style={{ width: `${p.completion_percentage}%` }} />
        </div>
      </div>
    </div>
  );
}

function MediaGrid({ items }) {
  const [active, setActive] = useState(null);
  if (!items.length) return <EmptyState icon={ImageIcon} title="No media yet" subtitle="Construction photos and videos will appear here as your home is built." />;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((m) => {
          const src = m.url.startsWith("http") ? m.url : fileUrl(m.url);
          return (
            <button key={m.id} onClick={() => setActive({ ...m, src })} className="group relative aspect-[4/3] overflow-hidden rounded-md border border-border bg-arq-surface" data-testid={`media-${m.id}`}>
              {m.type === "video" ? (
                <div className="absolute inset-0 flex items-center justify-center bg-arq-green/10"><PlayCircle className="h-10 w-10 text-arq-green" /></div>
              ) : (
                <img src={src} alt={m.caption} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              )}
              <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-[11px] text-white truncate">{m.caption}</p>
                <p className="text-[9px] text-white/70">{m.phase} · {fmtDate(m.date)}</p>
              </div>
            </button>
          );
        })}
      </div>
      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {active && (active.type === "video" ? (
            <video src={active.src} controls className="w-full" />
          ) : (
            <img src={active.src} alt={active.caption} className="w-full" />
          ))}
          {active && <div className="p-4"><p className="font-medium">{active.caption}</p><p className="text-xs text-muted-foreground">{active.phase} · {fmtDate(active.date)}</p></div>}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Build() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const { data, isLoading: dl } = useQuery({ queryKey: ["dashboard", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/dashboard`)).data, enabled: !!propertyId });
  const { data: milestones = [] } = useQuery({ queryKey: ["milestones", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/milestones`)).data, enabled: !!propertyId });
  const { data: media = [] } = useQuery({ queryKey: ["media", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}/media`)).data, enabled: !!propertyId });

  if (isLoading || dl) return <Loader label="Loading build progress" />;
  const p = data.property;

  return (
    <div className="space-y-8 arq-fade-up">
      <SectionHeading overline="Build" title="Build Progress" />
      <div className="bg-arq-card border border-border rounded-md p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-8">
        <ProgressRing value={p.completion_percentage} size={140} label="Complete" />
        <div className="flex-1 text-center sm:text-left">
          <p className="text-[11px] uppercase tracking-[0.2em] text-arq-accent font-semibold">Construction Progress</p>
          <h2 className="font-display text-3xl text-arq-ink mt-1">{p.completion_percentage}% — {data.current_phase?.name || p.status}</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-lg">Your {p.model} home is currently in the {data.current_phase?.name || "planning"} phase. Estimated completion {fmtDate(p.estimated_completion)}.</p>
        </div>
      </div>

      <Tabs defaultValue="phases">
        <TabsList data-testid="build-tabs">
          <TabsTrigger value="phases" data-testid="tab-phases">Phases</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
        </TabsList>
        <TabsContent value="phases" className="mt-6">
          <div className="bg-arq-card border border-border rounded-md p-6">
            {data.phases.map((p) => <PhaseRow key={p.id} p={p} />)}
          </div>
        </TabsContent>
        <TabsContent value="timeline" className="mt-6">
          <div className="bg-arq-card border border-border rounded-md p-6 sm:p-8">
            {milestones.length ? <Timeline milestones={milestones} /> : <EmptyState icon={Hammer} title="No milestones yet" />}
          </div>
        </TabsContent>
        <TabsContent value="media" className="mt-6">
          <MediaGrid items={media} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
